// src/components/admin/UserManagementWidget.js
import React, { useState } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Menu,
  MenuItem,
  Typography,
  Box,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Search as SearchIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';

const UserManagementWidget = ({ data }) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);

  if (!data || !Array.isArray(data)) {
    return (
      <Card sx={{ height: '100%' }}>
        <CardHeader title="👥 Gestión de Usuarios" />
        <CardContent>
          <Typography color="text.secondary">
            No hay datos de usuarios disponibles
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Filtrar usuarios
  const filteredUsers = data.filter(user => {
    const matchesSearch = user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'all' || user.status === statusFilter;
    
    return matchesSearch && matchesRole && matchesStatus;
  });

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleMenuOpen = (event, user) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'error',
      organizador: 'primary',
      cronometrador: 'secondary',
      entrenador: 'info',
      atleta: 'success',
      juez: 'warning',
      fotografo: 'default'
    };
    return colors[role] || 'default';
  };

  const getRoleIcon = (role) => {
    const icons = {
      admin: '👑',
      organizador: '📋',
      cronometrador: '⏱️',
      entrenador: '💪',
      atleta: '🏃‍♀️',
      juez: '⚖️',
      fotografo: '📸'
    };
    return icons[role] || '👤';
  };

  const handleUserAction = (action, user) => {
    console.log(`${action} usuario:`, user);
    handleMenuClose();
    
    switch (action) {
      case 'edit':
        // Lógica para editar usuario
        break;
      case 'block':
        // Lógica para bloquear/desbloquear usuario
        break;
      case 'delete':
        // Lógica para eliminar usuario
        break;
      case 'email':
        // Lógica para enviar email
        break;
      default:
        break;
    }
  };

  return (
    <Card sx={{ height: '100%' }}>
      <CardHeader
        title="👥 Gestión de Usuarios"
        subheader={`${filteredUsers.length} usuarios encontrados`}
        action={
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            size="small"
            onClick={() => console.log('Agregar usuario')}
          >
            Nuevo Usuario
          </Button>
        }
      />
      
      <CardContent>
        {/* Filtros y búsqueda */}
        <Box sx={{ mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <TextField
            size="small"
            placeholder="Buscar por nombre o email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 250 }}
          />
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Rol</InputLabel>
            <Select
              value={roleFilter}
              label="Rol"
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
              <MenuItem value="organizador">Organizador</MenuItem>
              <MenuItem value="atleta">Atleta</MenuItem>
              <MenuItem value="entrenador">Entrenador</MenuItem>
              <MenuItem value="juez">Juez</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Estado</InputLabel>
            <Select
              value={statusFilter}
              label="Estado"
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">Todos</MenuItem>
              <MenuItem value="active">Activo</MenuItem>
              <MenuItem value="inactive">Inactivo</MenuItem>
              <MenuItem value="blocked">Bloqueado</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Tabla de usuarios */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Usuario</TableCell>
                <TableCell>Rol</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Fecha Registro</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((user) => (
                  <TableRow key={user.id} hover>
                    <TableCell>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Avatar 
                          src={user.avatar} 
                          sx={{ width: 32, height: 32 }}
                        >
                          {user.name[0]}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {user.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {user.email}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={`${getRoleIcon(user.role)} ${user.role}`}
                        color={getRoleColor(user.role)}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Chip
                        label={user.status}
                        color={user.status === 'active' ? 'success' : 'error'}
                        size="small"
                        variant="filled"
                      />
                    </TableCell>
                    
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(user.joinDate).toLocaleDateString('es-ES')}
                      </Typography>
                    </TableCell>
                    
                    <TableCell align="center">
                      <Tooltip title="Más opciones">
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuOpen(e, user)}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Paginación */}
        <TablePagination
          component="div"
          count={filteredUsers.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25]}
          labelRowsPerPage="Filas por página:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}–${to} de ${count !== -1 ? count : `más de ${to}`}`
          }
        />

        {/* Menu contextual */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
        >
          <MenuItem onClick={() => handleUserAction('view', selectedUser)}>
            <ViewIcon sx={{ mr: 1 }} fontSize="small" />
            Ver Perfil
          </MenuItem>
          <MenuItem onClick={() => handleUserAction('edit', selectedUser)}>
            <EditIcon sx={{ mr: 1 }} fontSize="small" />
            Editar
          </MenuItem>
          <MenuItem onClick={() => handleUserAction('email', selectedUser)}>
            <EmailIcon sx={{ mr: 1 }} fontSize="small" />
            Enviar Email
          </MenuItem>
          <MenuItem onClick={() => handleUserAction('block', selectedUser)}>
            <BlockIcon sx={{ mr: 1 }} fontSize="small" />
            {selectedUser?.status === 'active' ? 'Bloquear' : 'Activar'}
          </MenuItem>
          <MenuItem 
            onClick={() => handleUserAction('delete', selectedUser)}
            sx={{ color: 'error.main' }}
          >
            <DeleteIcon sx={{ mr: 1 }} fontSize="small" />
            Eliminar
          </MenuItem>
        </Menu>

        {/* Estadísticas rápidas */}
        <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            📊 Resumen: {data.filter(u => u.status === 'active').length} activos, {' '}
            {data.filter(u => u.status === 'inactive').length} inactivos, {' '}
            {data.filter(u => u.role === 'atleta').length} atletas registrados
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default UserManagementWidget;